import React from "react";

import { AdvancedArticleListItemDetailEntity } from "@/mk2/apps/blogs/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    RECIPE_LIST_ITEMS,
    RECIPE_SERVINGS_COUNT_number,
    UNIT_CUP,
    UNIT_DROP,
    UNIT_PACK,
    UNIT_PINCH,
    UNIT_SLICE,
    UNIT_TABLESPOON,
    UNIT_TEASPOON,
    UNIT_TO_TASTE,
} from "@/mk/autogenerated/translations/RecipeIngredients.8e1ec383afbb6bf760ee47a0760864cf";

import styles from "./RecipeIngredients.mscss";

export const UNITS_WITH_HIDDEN_AMOUNT = ["to-taste", "pinch"];

export const RECIPE_UNIT_OPTIONS = [
    // most used
    { label: "ks", value: "amt" },
    { label: "g", value: "g" },
    { label: "ml", value: "ml" },

    // used
    { label: "mg", value: "mg" },
    { label: "kg", value: "kg" },
    { label: "liter", value: "liter" },

    // ret. units :)
    { label: UNIT_CUP, value: "cup" },

    { label: UNIT_TABLESPOON, value: "tablespoon" },
    { label: UNIT_TEASPOON, value: "teaspoon" },
    { label: UNIT_TO_TASTE, value: "to-taste" },
    { label: UNIT_DROP, value: "drop" },
    { label: UNIT_PINCH, value: "c" },
    { label: UNIT_SLICE, value: "slice" },
    { label: UNIT_PACK, value: "pack" },
];

const STEP = 0.5;

/**
 * Formátovanie množstva:
 * - zaokrúhlenie na 1 desatinné miesto
 * - odstránenie .0
 * - bodka → čiarka
 */
export function formatAmount(amount?: number) {
    if (amount === null || amount === undefined) return "";

    const rounded = Math.round(amount * 10) / 10;
    let str = String(rounded).trim();

    str = str.replace(/(\.0+)$/, "");
    str = str.replace(".", ",");

    return str;
}

const translateUnit = (unit: string) => {
    const found = RECIPE_UNIT_OPTIONS.find((u) => u.value === unit);
    return found ? found.label : unit;
};

function getInitialDynServCount(servingsCount?: number | null, servingsNote?: string) {
    if (typeof servingsCount === "number" && servingsCount > 0) {
        return servingsCount;
    }

    if (servingsNote) {
        const match = servingsNote.match(/(\d+(?:[.,]\d+)?)/);

        if (match) {
            const value = parseFloat(match[1].replace(",", "."));
            if (!isNaN(value) && value > 0) {
                return value;
            }
        }
    }

    return 1;
}

interface RecipeIngredientsGroupProps {
    items: AdvancedArticleListItemDetailEntity[];
    initialServingsCount: number;
    servingsCount?: number | null;
    servingsNote?: string;
    forceDefaultTitle?: boolean;
}

const RecipeIngredientsGroup = ({
    items,
    initialServingsCount,
    servingsCount,
    servingsNote,
    forceDefaultTitle = false,
}: RecipeIngredientsGroupProps) => {
    const [dynamicServingsCount, setDynamicServingsCount] = React.useState(initialServingsCount);

    const increase = () => {
        setDynamicServingsCount((prev) => prev + STEP);
    };

    const decrease = () => {
        setDynamicServingsCount((prev) => Math.max(STEP, prev - STEP));
    };

    const renderServings = () => {
        if (typeof servingsCount === "number") {
            return interpolate(RECIPE_SERVINGS_COUNT_number, {
                count: dynamicServingsCount,
                number: dynamicServingsCount,
            });
        }

        if (servingsNote) {
            const match = servingsNote.match(/(\d+(?:[.,]\d+)?)/);
            if (!match || match.index === undefined) {
                return `${formatAmount(dynamicServingsCount)}x ${servingsNote}`;
            }

            const before = servingsNote.slice(0, match.index);
            const after = servingsNote.slice(match.index + match[0].length);

            return `${before}${formatAmount(dynamicServingsCount)}${after}`;
        }

        return null;
    };

    const coef = dynamicServingsCount / initialServingsCount;
    const listTitle = items[0]?.list?.title;

    return (
        <div className={styles.RecipeIngredients__group}>
            {/* GROUP HEADING */}
            <div className={styles.RecipeIngredients__groupHeading}>
                <span className={styles.RecipeIngredients__groupHeading__title}>
                    {forceDefaultTitle ? RECIPE_LIST_ITEMS : listTitle || RECIPE_LIST_ITEMS}
                </span>

                {(servingsCount || servingsNote) && (
                    <div className={styles.RecipeIngredients__groupHeading__servings}>
                        <b>{renderServings()}</b>{" "}
                        <button
                            type="button"
                            onClick={decrease}
                            disabled={dynamicServingsCount <= STEP}
                        >
                            −
                        </button>
                        <button type="button" onClick={increase}>
                            +
                        </button>
                    </div>
                )}
            </div>

            {/* INGREDIENT LIST */}
            <div className={styles.RecipeIngredients__list}>
                {items.map((detail) => (
                    <div key={detail.id} className={styles.RecipeIngredients__list__item}>
                        <div className={styles.RecipeIngredients__list__item__firstRow}>
                            <span className={styles.RecipeIngredients__list__item__name}>
                                {detail.item.wikiArticleUrl ? (
                                    <a
                                        href={detail.item.wikiArticleUrl}
                                        className={styles.RecipeIngredients__list__item__nameLink}
                                    >
                                        {detail.item.name}
                                    </a>
                                ) : (
                                    detail.item.name
                                )}
                            </span>{" "}
                            <span className={styles.RecipeIngredients__list__item__amount}>
                                {!UNITS_WITH_HIDDEN_AMOUNT.includes(detail.unit) && (
                                    <>{formatAmount(detail.amount * coef)} </>
                                )}
                                {translateUnit(detail.unit)}
                            </span>
                        </div>

                        {detail.item.description && (
                            <span className={styles.RecipeIngredients__list__item__description}>
                                {detail.item.description}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const RecipeIngredients = ({
    advancedArticleListItemDetails,
    servingsCount,
    servingsNote,
}: {
    advancedArticleListItemDetails: AdvancedArticleListItemDetailEntity[];
    servingsCount?: number | null;
    servingsNote?: string;
}) => {
    const initialDynServCount = getInitialDynServCount(servingsCount, servingsNote);

    const groups = advancedArticleListItemDetails.reduce(
        (
            acc: Record<string, AdvancedArticleListItemDetailEntity[]>,
            item: AdvancedArticleListItemDetailEntity,
        ) => {
            const listId = item.list?.id ?? "unknown";

            if (!acc[listId]) {
                acc[listId] = [];
            }

            acc[listId].push(item);
            return acc;
        },
        {},
    );

    Object.keys(groups).forEach((listId) => {
        groups[listId].sort((a, b) => a.order - b.order);
    });

    return (
        <div className={styles.RecipeIngredients}>
            {Object.keys(groups).map((listId) => (
                <RecipeIngredientsGroup
                    key={listId}
                    items={groups[listId]}
                    initialServingsCount={initialDynServCount}
                    servingsCount={servingsCount}
                    servingsNote={servingsNote}
                    forceDefaultTitle={Object.keys(groups).length === 1}
                />
            ))}
        </div>
    );
};
